import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useBackNavigate } from '@/shared/hooks/use-back-navigate';
import {
  Badge,
  Button,
  Select,
  PageContainer,
} from '@react-apps/design-system';
import { useSubjects } from '../hooks/use-subjects';
import {
  useSubjectView,
  useAddMembership,
  useRemoveMembership,
} from '../hooks/use-subject-views';
import {
  ErrorState,
  Field,
  Mono,
  SectionCard,
  getApiErrorMessage,
} from '../display';
import { useCanWriteSubjects } from '../hooks/use-content-permissions';

const SUBJECTS_LIMIT = 200;

export function SubjectViewDetailPage() {
  const { viewId } = useParams<{ viewId: string }>();
  const { data, isLoading, isError, error } = useSubjectView(viewId);

  const [subjectId, setSubjectId] = useState('');
  const [isLeaf, setIsLeaf] = useState(false);

  const { data: subjectsPage } = useSubjects({
    page: 1,
    perPage: SUBJECTS_LIMIT,
  });
  const addMembership = useAddMembership(viewId ?? '');
  const removeMembership = useRemoveMembership(viewId ?? '');
  const canWrite = useCanWriteSubjects();

  const subjects = useMemo(() => subjectsPage?.items ?? [], [subjectsPage]);
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of subjects) map.set(s.id, s.name);
    return map;
  }, [subjects]);

  if (isLoading) {
    return (
      <PageContainer maxWidth="4xl" spacing="none">
        <p className="text-(--color-text-secondary) text-sm">Carregando…</p>
      </PageContainer>
    );
  }

  if (isError || !data) {
    return (
      <PageContainer maxWidth="4xl" spacing="sm">
        <BackLink />
        <ErrorState
          message={getApiErrorMessage(error, 'Visão não encontrada.')}
        />
      </PageContainer>
    );
  }

  const { view, members } = data;
  const canAdd = subjectId !== '' && !addMembership.isPending;

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAdd) return;
    addMembership.mutate(
      { subjectId, isLeaf },
      {
        onSuccess: () => {
          setSubjectId('');
          setIsLeaf(false);
        },
      },
    );
  };

  return (
    <PageContainer maxWidth="4xl">
      <BackLink />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-(--color-text-primary) text-xl font-semibold">
            {view.name}
          </h1>
          <p className="text-(--color-text-secondary) mt-1 text-sm">
            Tenant <Mono>{view.tenant}</Mono>
          </p>
        </div>
        <Link
          to={`/content/graph?view=${view.id}`}
          className="border-(--color-border) text-(--color-text-primary) hover:bg-(--color-bg-secondary) shrink-0 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors"
        >
          Ver mapa visual →
        </Link>
      </div>

      <SectionCard title="Dados da visão">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Nome" value={view.name} />
          <Field label="Tenant" value={<Mono>{view.tenant}</Mono>} />
          <Field label="ID" value={<Mono>{view.id}</Mono>} />
        </div>
      </SectionCard>

      <SectionCard title="Assuntos da visão">
        {canWrite && (
          <form
            onSubmit={handleAdd}
            className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_auto_auto]"
          >
            <Select
              label="Assunto"
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
              options={[
                { value: '', label: 'Selecione…' },
                ...subjects.map((s) => ({ value: s.id, label: s.name })),
              ]}
            />
            <label className="text-(--color-text-secondary) flex items-center gap-2 pb-2 text-sm">
              <input
                type="checkbox"
                checked={isLeaf}
                onChange={(e) => setIsLeaf(e.target.checked)}
              />
              Folha
            </label>
            <Button type="submit" disabled={!canAdd}>
              Adicionar
            </Button>
          </form>
        )}

        {addMembership.error && (
          <p className="text-sm text-red-600">
            {getApiErrorMessage(addMembership.error, 'Falha ao adicionar')}
          </p>
        )}

        {members.length === 0 ? (
          <p className="text-(--color-text-secondary) text-sm">
            Nenhum assunto nesta visão.
          </p>
        ) : (
          <ul className="divide-(--color-border) divide-y">
            {members.map((m) => (
              <li
                key={m.id}
                className="flex items-center justify-between gap-3 py-2"
              >
                <div className="flex flex-col">
                  <span className="text-(--color-text-primary) text-sm">
                    {nameById.get(m.subjectId) ?? '—'}{' '}
                    {m.isLeaf && <Badge variant="info">Folha</Badge>}
                  </span>
                  <Mono>{m.subjectId}</Mono>
                </div>
                {canWrite && (
                  <Button
                    variant="ghost"
                    disabled={removeMembership.isPending}
                    onClick={() => removeMembership.mutate(m.subjectId)}
                  >
                    Remover
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </PageContainer>
  );
}

function BackLink() {
  const goBack = useBackNavigate('/content/views');
  return (
    <button
      type="button"
      onClick={goBack}
      className="text-(--color-text-secondary) hover:text-(--color-text-primary) text-sm"
    >
      ← Voltar para Visões
    </button>
  );
}
